import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IDataFilterStateHandler from "features/shared/components/dataFilter/IDataFilterStateHandler";
import IPaginationStateHandler from "features/shared/components/pagination/IPaginationStateHandler";
import IDataFilterDescriptor from "features/shared/components/dataFilter/IDataFilterDescriptor";
import IDataGridSettings from "../IDataGridSettings";
import { useEffect, useState } from "react";
import SortingItemDescriptor from "features/shared/components/sorting/models/SortingItemDescriptor";
import { ISortingStateHandler } from "../../sorting/handlers/ISortingStateHandler";
import { SortingDirection } from "../../sorting/models/SortingDirection";
import IDataGridService from "../IDataGridService";
import { getPaginationDefaults } from "features/shared/helpers/queryParams";
import ConditionalExecute from "data/models/ConditionalExecute";

export const useStateDataGrid = (
  pageSize?: number,
  filterJson?: string,
  sortingJson?: string
) => {
  //#region DI
  const dataFilterStateHandler = useInjection<IDataFilterStateHandler>(IocTypes.DataFilterStateHandler);
  const paginationStateHandler = useInjection<IPaginationStateHandler>(IocTypes.PaginationStateHandler);
  const sortingStateHandler = useInjection<ISortingStateHandler>(IocTypes.SortingStateHandler);
  const dataGridService = useInjection<IDataGridService>(IocTypes.DataGridService);
  //#endregion DI

  //#region  STATE
  const [descriptors, setDescriptors] = useState<IDataFilterDescriptor[]>(filterJson ? dataFilterStateHandler.processFilter(filterJson, new URLSearchParams()) : []);
  const [sortingDescriptors, setSortingDescriptors] = useState<SortingItemDescriptor[]>(sortingJson ? sortingStateHandler.loadSortingDescriptors(sortingJson) : []);
  const [dataGrid, setDataGrid] = useState<IDataGridSettings>(dataGridService.getEmptySettings(pageSize));
  const [showFilter, setShowFilter] = useState<boolean>(false);
  //#endregion  STATE

  //#region  SIDE-EFFECTS
  // vychozi nastaveni pro DataGridSettings
  useEffect(() => {
    let paginationUrlParams: string = "";
    let filterUrlParams: string = "";
    let sortingUrlParams: string = "";

    // nastaveni strankovani
    if (pageSize) {
      paginationUrlParams = getPaginationDefaults(pageSize).toString();
    }
    // nastaveni filtru
    if (filterJson) {
      let filterUrlSearchParams = new URLSearchParams();
      dataFilterStateHandler.setDescriptorValuesToUrlParams(descriptors, filterUrlSearchParams);
      filterUrlParams = filterUrlSearchParams.toString();
    }
    // nastaveni trizeni
    if (sortingJson) {
      sortingUrlParams = sortingStateHandler.getUrlParamsOfActiveSorting(sortingDescriptors);
    }

    const defaultSettings = dataGridService.getSettings(filterUrlParams, paginationUrlParams, sortingUrlParams, pageSize)
    setDataGrid(defaultSettings);
  }, []);
  //#region  SIDE-EFFECTS

  //#region HANDLERS
  const paginationPageChangeHandler = (pageNumber: number) => {
    // nastaveni nove stranky do UrlParams
    const paginationUrlSearchParams = paginationStateHandler.getUrlParamsOfActivePagination(pageNumber, pageSize!);
    // nastavaveni nove stranky pro DataGrid
    const { defaultPageSize, filtrUrlSearchParams, sortingUrlSearchParams } = dataGrid;
    const updatedGridSettings = dataGridService.getSettings(filtrUrlSearchParams, paginationUrlSearchParams.toString(), sortingUrlSearchParams, defaultPageSize);
    // odeslani aktualniho nastaveni (znovunacteni dat)
    setDataGrid(updatedGridSettings);
  };

  const filterApplyHandler = () => {
    // nacteni vybranych hodnot do UrlParams
    let filterUrlSearchParams = new URLSearchParams();
    dataFilterStateHandler.setDescriptorValuesToUrlParams(descriptors, filterUrlSearchParams);
    // nastaveni noveho filtru pro DataGrid
    const { defaultPageSize, sortingUrlSearchParams } = dataGrid;
    const updatedGridSettings = dataGridService.getSettings(filterUrlSearchParams.toString(), undefined, sortingUrlSearchParams, defaultPageSize);
    // odeslani aktualniho nastaveni (znovunacteni dat)
    setDataGrid(updatedGridSettings);
    // skryti filtru
    setShowFilter(false);
  };

  const filterResetHandler = () => {
    // reset UrlParams (filtru) pro DataGrid
    const { sortingUrlSearchParams, defaultPageSize } = dataGrid;
    let updatedGridSettings = dataGridService.getSettings(undefined, undefined, sortingUrlSearchParams, defaultPageSize);
    setDataGrid(updatedGridSettings);
    // reset stavu samotneho filtru (nacteni defaultniho filtru)
    const defaultDescs = dataFilterStateHandler.processFilter(filterJson!, new URLSearchParams()); // TODO: upravit metodu aby JSON string byl optional
    setDescriptors(defaultDescs);  
    // skryti filtru
    setShowFilter(false);
  };

  const sortingChangeHandler = (name: string, direction: SortingDirection) => { 
    // zmena zpusobu trideni
    sortingStateHandler.changeSorting(name, direction, sortingDescriptors, setSortingDescriptors);
    // nastaveni noveho trideni pro DataGrid
    const sortingUrlSearchParams = sortingStateHandler.getUrlParamsOfActiveSorting(sortingDescriptors);
    const { defaultPageSize, filtrUrlSearchParams } = dataGrid;
    const updatedGridSettings = dataGridService.getSettings(filtrUrlSearchParams, undefined, sortingUrlSearchParams, defaultPageSize);
    // odeslani aktualniho nastaveni (znovunacteni dat)
    setDataGrid(updatedGridSettings);
  }
  //#endregion HANDLERS

  //#region FUNCTIONS
  const isApplyFilter = () => {
    return dataGrid.filtrUrlSearchParams !== "" 
    && dataGrid.filtrUrlSearchParams !== undefined;
  }
  //#endregion FUNCTIONS

  //#region HELPERS
  const conditionalRefresh: ConditionalExecute = {
    apply: true,
    condition: () => (dataGrid.computedUrlSearchParams ? true : false),
  };
  //#endregion HELPERS

  //#region RETURN
  return {
    // state
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    sortingDescriptors,
    // handlers
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    sortingChangeHandler,
    // functions
    isApplyFilter,
    // helpers
    conditionalRefresh
  };
  //#endregion RETURN
}
